/**
 * URL Security Utilities
 *
 * This module provides utilities to prevent Open Redirect vulnerabilities
 * by validating URLs before performing redirects.
 */

/**
 * Safely decodes a URL-encoded string, handling multiple levels of encoding.
 * Performs recursive decoding until no more encoding is detected to prevent
 * double/triple encoding bypass attacks.
 *
 * @param str - The potentially encoded string
 * @returns The fully decoded string, or the original if decoding fails
 */
function safeDecodeUrl(str: string): string {
  let decoded = str;
  let previousDecoded = "";
  let iterations = 0;
  const maxIterations = 5; // Prevent infinite loops from malformed input

  // Keep decoding until no change occurs or max iterations reached
  while (decoded !== previousDecoded && iterations < maxIterations) {
    previousDecoded = decoded;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      // If decoding fails (malformed URI), return what we have so far
      // This handles cases like incomplete percent encoding
      break;
    }
    iterations++;
  }

  return decoded;
}

/**
 * Validates that a URL is safe for redirection by ensuring it's a relative path
 * without protocol or domain information.
 *
 * @param url - The URL to validate
 * @returns The sanitized URL if safe, or '/' as a fallback
 *
 * @example
 * ```typescript
 * validateRelativeUrl('/dashboard'); // Returns: '/dashboard'
 * validateRelativeUrl('//evil.com'); // Returns: '/'
 * validateRelativeUrl('http://evil.com'); // Returns: '/'
 * ```
 */
export function validateRelativeUrl(url: unknown): string {
  if (!url || typeof url !== "string") {
    return "/";
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  const decodedUrl = safeDecodeUrl(trimmedUrl);
  const trimmedDecodedUrl = decodedUrl.trim();
  const decodedUrlNoWhitespace = trimmedDecodedUrl.replace(/\s/g, "");

  // Check if URL contains protocol (http://, https://, //, javascript:, data:, etc.)
  if (
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedDecodedUrl) || // Protocol check (e.g., http:, javascript:)
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decodedUrlNoWhitespace) || // Protocol with whitespace removed
    trimmedDecodedUrl.startsWith("//") || // Protocol-relative URL check
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedUrl) || // Also check original
    trimmedUrl.startsWith("//")
  ) {
    console.warn(
      `[Security] Blocked potentially malicious redirect URL: ${trimmedUrl} (decoded: ${trimmedDecodedUrl})`
    );
    return "/";
  }

  // Ensure the URL starts with a forward slash
  if (!trimmedUrl.startsWith("/")) {
    return `/${trimmedUrl}`;
  }

  return trimmedUrl;
}

/**
 * Validates that a URL is safe for opening in a new window/tab.
 * Allows only HTTPS URLs from trusted domains or relative URLs.
 *
 * @param url - The URL to validate
 * @param trustedDomains - Array of trusted domain patterns (e.g., ['example.com', 'sub.example.com'])
 * @returns true if the URL is safe to open, false otherwise
 *
 * @example
 * ```typescript
 * isUrlSafeForExternalOpen('https://trusted.com/page', ['trusted.com']); // Returns: true
 * isUrlSafeForExternalOpen('http://malicious.com', ['trusted.com']); // Returns: false
 * isUrlSafeForExternalOpen('/internal-page', []); // Returns: true (relative URLs allowed)
 * ```
 */
export function isUrlSafeForExternalOpen(
  url: unknown,
  trustedDomains: string[]
): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const trimmedUrl = url.trim();

  const decodedUrl = safeDecodeUrl(trimmedUrl);
  const trimmedDecodedUrl = decodedUrl.trim();
  const decodedUrlNoWhitespace = trimmedDecodedUrl.replace(/\s/g, "");

  // Allow relative URLs (check both original and decoded)
  if (trimmedUrl.startsWith("/") && !trimmedUrl.startsWith("//")) {
    if (trimmedDecodedUrl.startsWith("//")) {
      console.warn(
        `[Security] Blocked encoded protocol-relative URL: ${trimmedUrl} (decoded: ${trimmedDecodedUrl})`
      );
      return false;
    }
    return true;
  }

  // Block javascript:, data:, and other dangerous protocols
  if (
    /^(javascript|data|vbscript|file):/i.test(trimmedDecodedUrl) ||
    /^(javascript|data|vbscript|file):/i.test(decodedUrlNoWhitespace) ||
    /^(javascript|data|vbscript|file):/i.test(trimmedUrl)
  ) {
    console.warn(
      `[Security] Blocked dangerous protocol in URL: ${trimmedUrl} (decoded: ${trimmedDecodedUrl})`
    );
    return false;
  }

  // Parse the URL
  try {
    const parsedUrl = new URL(
      trimmedUrl,
      window?.location?.origin || "http://localhost"
    );
    // Only allow HTTPS (or HTTP in development/localhost)
    if (
      parsedUrl.protocol !== "https:" &&
      !(
        parsedUrl.protocol === "http:" &&
        (parsedUrl.hostname === "localhost" ||
          parsedUrl.hostname === "127.0.0.1")
      )
    ) {
      console.warn(`[Security] Blocked non-HTTPS URL: ${trimmedUrl}`);
      return false;
    }

    const isHostnameTrusted = trustedDomains.some(domain => {
      return (
        parsedUrl.hostname === domain ||
        parsedUrl.hostname.endsWith(`.${domain}`)
      );
    });

    if (!isHostnameTrusted) {
      console.warn(
        `[Security] Blocked URL from untrusted domain: ${trimmedUrl}`
      );
      return false;
    }

    if (trustedDomains.length === 0) {
      console.warn(
        "[Security] No trusted domains provided, denying external URL"
      );
      return false;
    }

    return true;
  } catch (error) {
    console.warn(`[Security] Invalid URL format: ${trimmedUrl}`, error);
    return false;
  }
}
